
import React, { useEffect, useRef } from "react";
import SearchForm from "./SearchForm";
import SearchResultsContainer from "./components/SearchResultsContainer";
import NoResultsMessage from "./NoResultsMessage";
import SearchError from "./SearchError";
import { useUnifiedSearch } from "@/hooks/search/useUnifiedSearch";

interface SearchWithListsProps {
  onOpenMovieDetails?: (tmdbId: number) => void;
  onExplicitClose?: () => void;
  registerSearchInput?: (ref: HTMLInputElement | null) => void;
}

const SearchWithLists: React.FC<SearchWithListsProps> = ({
  onOpenMovieDetails,
  onExplicitClose,
  registerSearchInput
}) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);
  
  const {
    query,
    handleInputChange,
    isResultsOpen,
    setIsResultsOpen,
    categorizedResults,
    totalHits,
    isSearching,
    showNoResultsMessage,
    searchError,
    handleKeyDown,
    closeResults,
    clearAll,
    triggerDirectSearch,
    searchResultsRef
  } = useUnifiedSearch({
    onExplicitClose
  });

  // Enhanced outside click detection that excludes clicks within search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isResultsOpen || !searchContainerRef.current) {
        return;
      }

      const target = event.target as Node;
      
      // Don't close if the click is within the search container
      if (searchContainerRef.current.contains(target)) {
        // Additional check: if we have search results and the click is within the results container, don't close
        if (searchResultsRef.current && searchResultsRef.current.contains(target)) {
          return; // Click is within search results, keep them open
        }
        
        // If click is on the search form itself (input, buttons), also don't close
        const searchForm = searchContainerRef.current.querySelector('form');
        if (searchForm && searchForm.contains(target)) {
          return;
        }
      }

      // Only close if the click is truly outside the search container
      if (!searchContainerRef.current.contains(target)) {
        closeResults();
        onExplicitClose?.();
      }
    };

    if (isResultsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isResultsOpen, closeResults, onExplicitClose, searchResultsRef]);

  const handleMovieSelect = (tmdbId: number) => {
    onOpenMovieDetails?.(tmdbId);
  };

  return (
    <div ref={searchContainerRef} className="relative w-full max-w-2xl">
      <SearchForm
        query={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClear={clearAll}
        onSearchClick={triggerDirectSearch}
        isLoading={isSearching}
        registerSearchInput={registerSearchInput}
      />

      {isResultsOpen && (
        <>
          {searchError ? (
            <SearchError error={searchError} />
          ) : showNoResultsMessage ? (
            <NoResultsMessage />
          ) : totalHits > 0 ? (
            <SearchResultsContainer
              sections={categorizedResults}
              query={query}
              onOpenMovieDetails={handleMovieSelect}
              resultsRef={searchResultsRef}
              onClose={closeResults}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default SearchWithLists;
